import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { PackagePlus, Plus } from 'lucide-react'

import type {
    AgriMaterialLotInReason,
    ListNotesParams,
    NoteStatus,
} from '@/lib/api/agri-material-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { Button } from '@/components/ui/button'
import {
    WarehouseNoteStatusBadge,
    warehouseNoteStatusLabel,
} from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    agriLotKeys,
    useAgriInNotes,
    useAgriInNotesInfinite,
} from '@/hooks/use-agri-material-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import { StockInNotePrintButton } from '@/components/warehouse/stock-in-note-print-button'
import { agriMaterialInNoteToPrintData } from '@/lib/print-adapters/stock-in-note-adapters'
import { agriMaterialLotApi } from '@/lib/api/agri-material-lot-api'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'agri_material_lots.read'
const PER_PAGE = 50

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Không dùng preset `noteStatusFilter` vì màn vật tư có bộ nhãn riêng
 * (`aml_notes_filter_*`, "Tất cả" chứ không phải "Tất cả trạng thái").
 */
const FILTERS = defineListFilters({
    status: selectFilter<NoteStatus | 'all'>({
        label: m.aml_notes_filter_status,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.aml_notes_filter_all() },
            { value: 'pending', label: m.sl_note_status_pending() },
            { value: 'approved', label: m.sl_note_status_approved() },
            { value: 'rejected', label: m.sl_note_status_rejected() },
            { value: 'cancelled', label: m.sl_note_status_cancelled() },
        ],
    }),
    reason: selectFilter<AgriMaterialLotInReason | 'all'>({
        label: m.aml_notes_filter_reason,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.aml_notes_filter_all() },
            {
                value: 'from_supplier',
                label: m.aml_in_reason_from_supplier(),
            },
            { value: 'adjustment', label: m.aml_in_reason_adjustment() },
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/stock-in-notes/',
)({
    component: AgriInNotesPage,
    validateSearch: FILTERS.validateSearch,
})

export function agriInReasonLabel(reason: AgriMaterialLotInReason | string) {
    if (reason === 'from_supplier') return m.aml_in_reason_from_supplier()
    // Phiếu do kiểm kê thực tế sinh — gọi đúng tên để phân biệt với phiếu
    // điều chỉnh do người dùng tự tạo.
    if (reason === 'from_stocktake_surplus') {
        return m.pl_reason_from_stocktake_surplus()
    }
    return m.aml_in_reason_adjustment()
}

function AgriInNotesPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return (
            <div className="p-6 text-sm text-muted-foreground">
                {m.aml_no_permission_view()}
            </div>
        )
    }
    return <Content />
}

function Content() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('agri_material_lots.write')
    const navigate = useNavigate({
        from: '/agricultural-materials/stock-in-notes/',
    })

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.status !== 'all' ? { status: values.status } : {}),
            ...(values.reason !== 'all' ? { reason: values.reason } : {}),
        }),
        [values.status, values.reason],
    )

    const params = useMemo<ListNotesParams>(
        () => ({ page, per_page: PER_PAGE, ...apiFilters }),
        [page, apiFilters],
    )
    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = useAgriInNotes(params, {
        enabled: !isMobile,
    })

    const infiniteParams = useMemo(
        () => ({ per_page: PER_PAGE, ...apiFilters }),
        [apiFilters],
    )
    const infinite = useAgriInNotesInfinite(infiniteParams, {
        enabled: isMobile,
    })
    const mobileNotes = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    const total = data?.meta?.total ?? 0

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {barProps.activeCount > 0
                    ? m.list_empty_filtered()
                    : m.aml_notes_empty()}
            </span>
            {barProps.activeCount > 0 ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<PackagePlus className="size-5 shrink-0" />}
                title={m.aml_pnk_title()}
                primaryActions={
                    canWrite ? (
                        <Button asChild>
                            <Link
                                to="/agricultural-materials/stock-in-notes/new"
                                className="w-full"
                            >
                                <Plus className="mr-1 size-4" />
                                {m.aml_notes_new_pnk()}
                            </Link>
                        </Button>
                    ) : undefined
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.aml_loading()}
                    </div>
                ) : mobileNotes.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileNotes.map((n) => (
                            <NoteListCard
                                actions={<PrintButton id={n.id} />}
                                key={n.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/agricultural-materials/stock-in-notes/$id',
                                        params: { id: n.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/agricultural-materials/stock-in-notes/$id"
                                        params={{ id: n.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                }
                                statusBadge={
                                    <WarehouseNoteStatusBadge
                                        status={n.status}
                                    />
                                }
                                reason={agriInReasonLabel(n.reason)}
                                totalLots={n.items_count ?? 0}
                                createdBy={n.created_by_name ?? undefined}
                                createdAt={n.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="h-11 w-full"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto rounded-md border bg-card md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.aml_notes_col_code()}</TableHead>
                            <TableHead>{m.aml_notes_col_status()}</TableHead>
                            <TableHead>{m.aml_notes_col_reason()}</TableHead>
                            <TableHead className="text-center">
                                {m.aml_notes_col_items()}
                            </TableHead>
                            <TableHead>{m.aml_notes_col_creator()}</TableHead>
                            <TableHead>{m.aml_notes_col_created()}</TableHead>
                            <TableHead className="w-16 text-center">
                                {m.sinp_print_btn()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.aml_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            notes.map((n) => (
                                <TableRow
                                    key={n.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/agricultural-materials/stock-in-notes/$id',
                                            params: { id: n.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/agricultural-materials/stock-in-notes/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <WarehouseNoteStatusBadge
                                            status={n.status}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {agriInReasonLabel(n.reason)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {n.items_count ?? 0}
                                    </TableCell>
                                    <TableCell>
                                        {n.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(n.created_at)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <PrintButton id={n.id} />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden items-center justify-between md:flex">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / PER_PAGE)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * PER_PAGE >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}

/** Nút in phiếu nhập kho cho 1 dòng danh sách — tải chi tiết phiếu rồi mới in
 *  vì API danh sách không trả dòng hàng. */
/** Nút in phiếu nhập kho cho 1 dòng danh sách — tải chi tiết phiếu rồi mới in
 *  vì API danh sách không trả dòng hàng. */
function PrintButton({ id }: { id: string }) {
    const qc = useQueryClient()
    return (
        <StockInNotePrintButton
            iconOnly
            loadNote={async () =>
                (
                    await qc.fetchQuery({
                        queryKey: agriLotKeys.inNoteDetail(id),
                        queryFn: () => agriMaterialLotApi.getInNote(id),
                    })
                ).data ?? null
            }
            toPrintData={(note) =>
                agriMaterialInNoteToPrintData(note, {
                    warehouseLabel: m.sinp_wh_agri(),
                    reasonLabel: agriInReasonLabel(note.reason),
                    statusLabel: warehouseNoteStatusLabel(note.status),
                })
            }
        />
    )
}
